<script>
  export let sceneName;
  export let annotationsList;
  export let previousSceneName;
  export let mainAltText;

  import { currentProgress, spotlightActive } from "../stores/scrollVars.js";
  import mq from "../stores/mq.js"; // here, defined laptop = 992px

  import { fade } from "svelte/transition";
  import { onMount } from "svelte";

  import ZoomWrapper from "./ZoomWrapper.svelte";
  import SceneDetail from "./SceneDetail.svelte";
  import SceneDetailCircles from "./SceneDetailCircles.svelte";
  import SceneImage from "./SceneImage.svelte";
  import SceneAnnotations from "./SceneAnnotations.svelte";

  // Import images
  import overall_view from "../img/tuiles_assemblage.png";
  import overall_view_mobile from "../img/tuiles_assemblage_800px.png"

  import dangers_1 from "../img/dangers_1.png";
  import dangers_2 from "../img/dangers_2.png";
  import dangers_3 from "../img/dangers_3.png";
  import dangers_4 from "../img/dangers_4.png";
  import dangers_5 from "../img/dangers_5.png";

  import new_buildings from "../img/not_enough.png";
  import new_buildings_mobile from "../img/not_enough_800px.png";

  import tuile1_zoom1 from "../img/tuile_1_zoom1.png";
  import tuile1_zoom2 from "../img/tuile_1_zoom2.png";
  import tuile1_zoom3 from "../img/tuile_1_zoom3.png";
  import tuile2_zoom1 from "../img/tuile_2_zoom1.png";
  import tuile2_zoom2 from "../img/tuile_2_zoom2.png";
  import tuile2_zoom3 from "../img/tuile_2_zoom3.png";
  import tuile3_zoom1 from "../img/tuile_3_zoom2.png";
  import tuile3_zoom2 from "../img/tuile_3_zoom1.png";
  import tuile4_zoom1 from "../img/tuile_4_zoom1.png";
  import tuile4_zoom2 from "../img/tuile_4_zoom2.png";
  import conclusion_view from "../img/tuile_conclusion_zoom.png";

  let nestedScenes = 0;
  let nestedSceneN = 0;

  // Scene directions
  const sceneList = {
    intro: {
      backdrop: "overviewCentered"
    },
    opening: {
      backdrop: "overview",
      annotationColour: "positive"
    },
    faults1: {
      backdrop: "overview",
      overlays: [dangers_1, dangers_2, dangers_3],
      annotationColour: "negative"
    },
    faults2: {
      backdrop: "overview",
      overlays: [dangers_5, dangers_4],
      annotationColour: "negative"
    },
    climate: {
      backdrop: "overview",
      overlays: [$mq.mobile ? new_buildings_mobile : new_buildings],
      annotationColour: "negative"
    },
    electricity1: {
      backdrop: "city"
    },
    electricity2: {
      backdrop: "city",
      details: [tuile1_zoom1, tuile1_zoom2, tuile1_zoom3, tuile1_zoom2, tuile1_zoom3, tuile1_zoom3, tuile1_zoom3],
      detailLoc: { top: "15%", left: "27%", width: "25%" }
    },
    roads1: {
      backdrop: "road"
    },
    roads2: {
      backdrop: "road",
      details: [tuile3_zoom1, tuile3_zoom2],
      detailLoc: { top: "55%", left: "15%", width: "20%" }
    },
    floods1: {
      backdrop: "coastal"
    },
    floods2: {
      backdrop: "coastal",
      details: [tuile2_zoom1, tuile2_zoom2, tuile2_zoom3],
      detailLoc: { top: "35%", left: "55%", width: "20%" }
    },
    netzero1: {
      backdrop: "netzero"
    },
    netzero2: {
      backdrop: "netzero",
      details: [tuile4_zoom1, tuile4_zoom2, tuile4_zoom2, tuile4_zoom2],//
      detailLoc: { top: "60%", left: "40%", width: "30%" }
    },
    recommendations: {
      backdrop: ["cityFinal", "overview"],
      details: [conclusion_view],
      detailLoc: { top: "15%", left: "55%", width: "20%" }
    }
  };

  const conclusionScene = "recommendations";
  const firstScene = "opening";

  const backdropList = {
    overviewCentered: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 2, top: "45%", left: "50%" } // this is the center of the zoom
    },
    overview: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 1, top: "0%", left: "0%" }
    },
    city: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 2, top: "0%", left: "31%" }
    },
    road: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 2, top: "80%", left: "5%" }
    },
    coastal: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 2, top: "40%", left: "75%" }
    },
    netzero: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 2, top: "100%", left: "60%" }
    },
    cityFinal: {
      baseImage: $mq.mobile ? overall_view_mobile : overall_view,
      zoomPos: { scale: 2.5, top: "10%", left: "110%" }
    }
  };

  const posDefault = { scale: 1, top: "0%", left: "0%" };


  const annotatePos = {
        downtown: { left: "70%", top: "10%", width: "30%" },
        power: { left: "2%", top: "5%", width: "30%" },
        rural: { left: "73%", top: "62%", width: "30%" },
        rural2: { left: "40%", top: "45%", width: "30%" },
        road: { left: "2%", top: "45%", width: "30%" },
        rail: { left: "65%", top: "70%", width: "30%" },
        coastal: { left: "70%", top: "20%", width: "30%" }
      };

  // This is for multiple pan/zooms within one scene:
  const getBg = function(sceneName, zoomProgress, minusOne = false){
    if (sceneList[sceneName]){
      let bg = sceneList[sceneName].backdrop;
      if (typeof bg == "string"){
        nestedScenes = 0;
        nestedSceneN = 0;
        return bg
      } else { // not the best, but just assume it's an array...
        let n = bg.length;
        let i = Math.floor(zoomProgress * n );
        if (!minusOne){
          nestedScenes = n;
          nestedSceneN = i;
          bg = bg[zoomProgress >= 1 ? n -1 : i];
        } else { // used when getting previousZoom
          bg = i >= 0 ? bg[zoomProgress >= 1 ? n -1 : (i - 1)] : undefined;
        }
        return bg;
      }
    } else {
      return ""
    }
  }
  // const getBg = (sceneName) => (sceneList[sceneName] ? sceneList[sceneName].backdrop : "");
  const getPos = (backdrop) => (backdropList[backdrop] ? backdropList[backdrop].zoomPos : posDefault);
  const getBaseImg = (backdrop) => (backdropList[backdrop] ? backdropList[backdrop].baseImage : overall_view);
  const getZoomImg = (backdrop) => (backdropList[backdrop] ? backdropList[backdrop].zoomImage : overall_view);
  const getOverlays = (sceneName) => (sceneList[sceneName] ? sceneList[sceneName].overlays || [] : []);
  const getScenesWithOverlays = () => Object.values(sceneList).filter((scene) => scene.overlays);
  const getDetails = (sceneName) => (sceneList[sceneName] ? sceneList[sceneName].details || [] : []);
  const getScenesWithDetails = () => Object.values(sceneList).filter((scene) => scene.details);
  const getDetailCircles = (sceneName) => (sceneList[sceneName] ? sceneList[sceneName].detailLoc : undefined);
  const getAnnotationColour = (sceneName) => (sceneList[sceneName] ? sceneList[sceneName].annotationColour || "neutral" : "neutral");

  let scenesWithOverlays = getScenesWithOverlays();
  let scenesWithDetails = getScenesWithDetails();
  scenesWithDetails.forEach(scene => {
    if (scene.backdrop == "netzero"){
      scene.detailColour = scene.details.map((d, i) => i == 0 ? 'neutral' : 'positive')
    } else if (scene.backdrop == "cityFinal"){
      scene.detailColour = scene.details.map((d, i) => 'neutral')
    } else if (scene.backdrop == "road"){
      scene.detailColour = scene.details.map((d, i) => i == 0 ? 'negative' : 'positive')
    } else {
      scene.detailColour = scene.details.map((d, i) => i == 0 ? 'neutral' : 'negative')
    }
  })

  scenesWithOverlays.forEach(scene => {
    scene.overlayOpacity = scene.overlays.map(d => d === new_buildings ? 1 : 0.7)
  })

  $: currentBackdrop = getBg(sceneName, zoomProgress);
  // $: currentBackdrop = getBg(sceneName);
  $: previousBackdrop = getBg(previousSceneName);
  $: currentOverlays = getOverlays(sceneName);
  $: currentDetails = getDetails(sceneName);
  $: currentCircles = getDetailCircles(sceneName);

  $: annotationColour = getAnnotationColour(sceneName);

  $: baseImage = getBaseImg(currentBackdrop);
  $: zoomImage = getZoomImg(currentBackdrop);
  $: zoom = getPos(currentBackdrop);
  $: previousZoom = nestedScenes > 1 ? getPos(getBg(sceneName, zoomProgress, true) ? getBg(sceneName, zoomProgress, true) : previousBackdrop): getPos(previousBackdrop);
  $: zoomFinishedBy = sceneName == firstScene ? 0.5 : (sceneName == conclusionScene ? 0.9 : 1);
  $: zoomProgress = $currentProgress > zoomFinishedBy
        ? 1
        : $currentProgress / (zoomFinishedBy);
  // $: console.log({ zoom, currentBackdrop, sceneName, annotationsList });
  $: zoomProgressB = nestedScenes > 1 ? zoomProgress * nestedScenes - ((nestedSceneN + 1) - 1) : zoomProgress;
</script>

<!-- <div id="debugger">
Current zoom: {zoom.top}, {zoom.left}
Previous zoom: {previousZoom.top}, {previousZoom.left} <br/>
{$currentProgress}
{zoomProgress}
{zoomProgressB} <br/>
{nestedScenes}, {nestedSceneN}
</div> -->
<ZoomWrapper zoomEnd={zoom} zoomStart={previousZoom} progress={zoomProgressB}>
  <div class="scene-wrapper">
    <div class="scene-details-background" class:active={$spotlightActive} />
    {#if sceneName}
      <SceneImage {baseImage} {currentOverlays} {scenesWithOverlays} {mainAltText}/>
      <SceneAnnotations {annotationsList} {annotatePos} colour={annotationColour} startOn={sceneName === firstScene ? zoomFinishedBy * 0.7 : 0.05}/>
      <SceneDetailCircles circleSpecs={currentCircles} isActive={
        $currentProgress > (sceneName === conclusionScene ? 0.3 : 0.005) && $currentProgress < (sceneName === conclusionScene ? 0.9 : 0.7)} />
      {#if sceneName == "intro"}
      <!-- <svg class="bounce-arrow {$mq.classNames}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="27px" height="27px">
        <path d="M5 5 H235 L120 110z" />
      </svg> -->
      {/if}
    {/if}
  </div>
</ZoomWrapper>
<SceneDetail {currentDetails} {scenesWithDetails} outro={sceneName == conclusionScene} {mainAltText}/>

<style>
  /* #debugger {
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: var(--z-overlay);
    background-color: white;
  } */
  .scene-wrapper {
    position: relative;
    width: max-content;
    margin: 0 auto;
  }

  .scene-details-background {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
    transition: opacity 2s;
    pointer-events: none;
    background: hsla(var(--voila-bg_hsl), 0.9);
  }
  .scene-details-background.active {
    opacity: 1;
    transition: opacity 1s;
  }

  /* .bounce-arrow {
    position: absolute;
    content: "";
    animation: arrowbounce 1s ease-in-out 0s infinite alternate;
    transform: translateX(-50%);
    fill: white;
    stroke-width: 7;
    filter: url(#fTexture3);
    left: 50%;
    top: 60%;
    stroke: var(--voila-border);
    
  }

  .bounce-arrow.mq-laptop {
    left: 15%;
    top: 46%;
  }

  @keyframes arrowbounce {
    from {
      transform: translate(-50%, 0);
    }
    to {
      transform: translate(-50%, 10px);
    }
  } */
</style>
